import { type CSSProperties, type ReactNode, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { useAuth } from '@forbiz/web-kit'
import type { CatalogItem, OrderListItem } from '@forbiz/contracts'

import { ordersApi } from '../lib/orders.js'
import { DOW, buildCells, gridStartOf, localDateStr } from '../lib/calendar.js'
import { fetchKrHolidays, type HolidayMap } from '../lib/holidays.js'
import { bannersApi } from '../lib/banners.js'
import { BannerCarousel } from '../components/BannerCarousel.js'
import { ReferralSlide } from '../components/ReferralSlide.js'
import { Footer } from '../components/Footer.js'
import { defaultProductImage } from '../lib/productImage.js'
import { useAsync } from '../lib/useAsync.js'
import categoryFuneralArt from '../assets/category-funeral.png'
import categoryCelebrationArt from '../assets/category-celebration.png'
import categoryGiftArt from '../assets/category-gift.png'
import s from './HomePage.module.css'

// 알림 스트립 아이콘 — 이모지 대신 라인 아이콘(24 그리드, currentColor)으로 톤을 맞춘다.
// 색은 상위 배너(.alert 앰버 / .softAlert 그린)의 color를 그대로 물려받는다.
const sw = 1.8
const IC = {
  warn: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3.5 21 19H3l9-15.5z" stroke="currentColor" strokeWidth={sw} strokeLinejoin="round" />
      <path d="M12 10v4" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" />
      <circle cx="12" cy="16.6" r="0.4" fill="currentColor" stroke="currentColor" strokeWidth={sw} />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="15" rx="2" stroke="currentColor" strokeWidth={sw} />
      <path d="M3.5 9h17M8 3.5v3M16 3.5v3" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth={sw} />
      <path d="m8.5 12 2.5 2.5 4.5-5" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  card: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth={sw} />
      <path d="M3 10h18" stroke="currentColor" strokeWidth={sw} />
      <path d="M6.5 14.5h4" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" />
    </svg>
  ),
  bell: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6.5 17V11a5.5 5.5 0 0 1 11 0v6M4.5 17h15" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 20a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" />
    </svg>
  ),
  edit: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 20h4L18.5 9.5a2 2 0 0 0 0-2.83l-1.17-1.17a2 2 0 0 0-2.83 0L4 16v4z" stroke="currentColor" strokeWidth={sw} strokeLinejoin="round" />
      <path d="m13.5 6.5 4 4" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" />
    </svg>
  ),
} as const

/** 상품 둘러보기 카테고리 타일 아트(근조 화환·축하 화환·난). 유형별 실사 아이콘
 *  (파스텔 배경 위 대표 상품)을 번들 URL로 불러와 타일 background-image로 쓴다. */
const CATEGORY_ART: Record<OrderListItem['occasionType'], string> = {
  FUNERAL: categoryFuneralArt,
  CELEBRATION: categoryCelebrationArt,
  GIFT: categoryGiftArt,
}

/**
 * 기업 담당자 홈 — 4블록 개편 (BIZ-99). 헤더/네비(Shell)는 그대로 두고 그 아래 콘텐츠만 재구성한다.
 *   1. 주문하기(히어로): 부고 링크 붙여넣기 → 불러오기(/orders/new 파싱 흐름) + 빠른 재주문 칩.
 *   2. 알림 스트립: '지금 챙길 것'(확인 필요·오늘/내일 배송)을 한 줄로 흡수.
 *   3. 최근 주문 + 캘린더(리스트/캘린더 토글). 캘린더는 라이브러리 없는 경량 자체 구현.
 *   3.5 상품 둘러보기: 전역 카탈로그 가로 스크롤 + 전체 보기.
 *   4. 내 디지털 상품: 기능 미완이라 '준비 중' 점선 플레이스홀더만 노출(기획 확정 시 실제 목록으로 교체).
 * 데이터는 기존 훅(ordersApi.home·catalog·list)을 재사용한다. 업체는 홈 대신 배정 목록(/orders)으로.
 */

const occasionLabel: Record<OrderListItem['occasionType'], string> = {
  FUNERAL: '근조',
  CELEBRATION: '축하',
  GIFT: '난·기타',
}

/** 상품 둘러보기 카테고리 카드(BIZ-110). 상품 데이터의 실제 축은 occasion 3종이라
 *  세부 상황(결혼·개업·승진 등)은 축하로 묶어 hint로 안내한다. 순서 = 카드 노출 순서. */
const CATEGORY_META: { occ: OrderListItem['occasionType']; title: string; hint: string }[] = [
  { occ: 'FUNERAL', title: '추모·근조', hint: '근조화환' },
  { occ: 'CELEBRATION', title: '축하·개업', hint: '결혼 · 행사 · 승진' },
  { occ: 'GIFT', title: '난·선물', hint: '동양란 · 기타' },
]

/** companyFacing 상태 문구 → D1 상태 pill(stpill) 클래스.
 *  진행 단계가 한눈에 보이게 색을 나눈다. 검사 순서 주의 —
 *  '배송 완료'는 '완료'가 '배송'보다 먼저 잡혀야 하고, '배송 문제'는 '문제'가
 *  '배송'보다 먼저 잡혀야 경보색이 된다. */
function statusPillClass(status: string): string {
  if (status.includes('취소')) return 'stpill st-muted' // 취소 완료 — 종료(회색)
  if (status.includes('문제')) return 'stpill st-stop' // 배송 문제 — 경보(빨강)
  if (status.includes('확인 필요')) return 'stpill st-warn' // 앰버
  if (status.includes('완료')) return 'stpill st-ok' // 배송 완료 — 차분한 그린
  if (status.includes('배송')) return 'stpill st-live' // 배송 중 — 브라이트 그린(오는 중)
  if (status.includes('준비')) return 'stpill st-prep' // 준비 중 — 슬레이트
  if (status.includes('작성')) return 'stpill st-muted' // 작성 중 — 회색
  return 'stpill st-info' // 주문 접수 등 — 티일(완료 그린과 색상환 분리)
}

function fmtDate(v: string | null): string {
  if (!v) return ''
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return v
  return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`
}


/** 유형별 라인 아이콘 — 최근 주문 썸네일(A2). 근조는 절제 톤. */
const OCCASION_ICON: Record<OrderListItem['occasionType'], ReactNode> = {
  GIFT: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 21v-9" />
      <path d="M12 12c0-3.3 2.2-5.5 5.5-5.5C17.5 9.8 15.3 12 12 12z" />
      <path d="M12 15c0-2.8-1.9-4.5-4.5-4.5C7.5 13.3 9.4 15 12 15z" />
    </svg>
  ),
  CELEBRATION: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4.5l1.5 4L17.5 10l-4 1.5L12 15.5 10.5 11.5 6.5 10l4-1.5z" />
      <path d="M18 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
    </svg>
  ),
  FUNERAL: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="2.2" />
      <ellipse cx="12" cy="6.6" rx="1.7" ry="2.9" />
      <ellipse cx="12" cy="6.6" rx="1.7" ry="2.9" transform="rotate(72 12 12)" />
      <ellipse cx="12" cy="6.6" rx="1.7" ry="2.9" transform="rotate(144 12 12)" />
      <ellipse cx="12" cy="6.6" rx="1.7" ry="2.9" transform="rotate(216 12 12)" />
      <ellipse cx="12" cy="6.6" rx="1.7" ry="2.9" transform="rotate(288 12 12)" />
    </svg>
  ),
}

/** 최근 주문 썸네일 — 유형별 라인 아이콘 + 저채도 틴트 원(A2). 근조는 절제 톤(슬레이트).
 *  카테고리 실사 이미지는 파스텔 배경이 박혀 있어 작은 리스트에선 유형끼리 색이 튀므로 아이콘으로 통일한다. */
function OccasionThumb({ occasion }: { occasion: OrderListItem['occasionType'] }) {
  return (
    <span className={`${s.othumb} ${s[`fb_${occasion}`]}`} aria-hidden="true">
      {OCCASION_ICON[occasion]}
    </span>
  )
}

function OrderCard({ o, onOpen }: { o: OrderListItem; onOpen: () => void }) {
  const sub = o.desiredDate ? `${fmtDate(o.desiredDate)} 희망` : ''
  return (
    <button type="button" className={s.ocard} aria-label={`${o.title || '주문'} 상세 보기`} onClick={onOpen}>
      <OccasionThumb occasion={o.occasionType} />
      <span className={s.oinfo}>
        <span className={s.otitle}>{o.title || '주문'}</span>
        <span className={s.ometa}>
          {sub ? <span className={s.osub}>{sub}</span> : null}
          <span className={statusPillClass(o.companyFacingStatus)}>{o.companyFacingStatus}</span>
        </span>
      </span>
    </button>
  )
}

export function HomePage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const isCompany = user?.role === 'COMPANY_ADMIN' || user?.role === 'COMPANY_MEMBER'
  const companyId = user?.companyId ?? null

  const [view, setView] = useState<'list' | 'calendar'>('list')
  const [calMonth, setCalMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1))
  const [selectedDay, setSelectedDay] = useState<string | null>(null)

  const { data, loading } = useAsync(() => (isCompany ? ordersApi.home() : Promise.resolve(null)), [isCompany])

  const bannersAsync = useAsync(
    () => (isCompany ? bannersApi.list() : Promise.resolve({ items: [] })),
    [isCompany],
  )

  const catalog = useAsync(
    () => (isCompany && companyId ? ordersApi.catalog(companyId) : Promise.resolve({ items: [] })),
    [isCompany, companyId],
  )

  const gridStart = useMemo(() => gridStartOf(calMonth), [calMonth])
  const cells = useMemo(() => buildCells(gridStart), [gridStart])
  const calFrom = localDateStr(gridStart)
  const calTo = useMemo(() => {
    const end = new Date(gridStart)
    end.setDate(gridStart.getDate() + 41)
    return localDateStr(end)
  }, [gridStart])
  // 캘린더는 열렸을 때만 그 달 범위(희망일 from~to)로 주문을 불러온다. 기존 목록 API 재사용.
  const calData = useAsync(
    () =>
      isCompany && view === 'calendar'
        ? ordersApi.list({ from: calFrom, to: calTo, limit: 100 })
        : Promise.resolve(null),
    [isCompany, view, calFrom, calTo],
  )
  // 공휴일 — 그리드가 연말·연초에 두 해에 걸칠 수 있어 범위의 양끝 연도를 함께 조회한다.
  const holidaysAsync = useAsync(
    () =>
      isCompany && view === 'calendar'
        ? fetchKrHolidays([...new Set([Number(calFrom.slice(0, 4)), Number(calTo.slice(0, 4))])])
        : Promise.resolve<HolidayMap>({}),
    [isCompany, view, calFrom, calTo],
  )
  const holidayMap: HolidayMap = holidaysAsync.data ?? {}

  // 업체는 홈 대시보드가 아니라 배정 주문 목록으로.
  if (user && !isCompany) return <Navigate to="/orders" replace />

  const attention = data?.attention ?? []
  const recent = data?.recent ?? []
  const today = data?.deliveringToday ?? 0
  const tomorrow = data?.deliveringTomorrow ?? 0
  const soon = data?.deliveringSoon ?? 0
  const unpaid = data?.unpaidCount ?? 0
  const products = catalog.data?.items ?? []

  // 카테고리 카드: 유형별 개수. 상품 없는 유형은 카드도 안 낸다(빈 카드 방지).
  // 원형 타일은 유형별 디자인 일러스트(CATEGORY_ART)를 쓴다 — 실제 상품 사진은 흰 꽃·흰 화분이
  // 많아 72px 원에 cover-crop하면 흰 부분만 보여 빈 원처럼 됐다. 실사진은 아래 상품 카드에서 보인다.
  const categories = CATEGORY_META.map((c) => ({
    ...c,
    count: products.filter((p) => p.occasionType === c.occ).length,
  })).filter((c) => c.count > 0)

  const goOrder = () => navigate('/orders/new')

  // 배너(BIZ-111): 내부 경로(/…)는 라우터로, 외부 URL은 새 탭. 링크 없으면 비클릭.
  const bannerItems = bannersAsync.data?.items ?? []
  const openBanner = (link: string | null) => {
    if (!link) return
    if (link.startsWith('/')) navigate(link)
    else window.open(link, '_blank', 'noopener')
  }

  // 알림은 두 결로 나눈다 — 긴급(확인 필요·배송 문제, 앰버 스트립)과 정보(오늘/내일 배송, 차분한 줄).
  // 업체 거절은 여기 안 낀다(BIZ-118) — attention 자체에서 빠져 '준비 중'으로만 보인다.
  // 둘은 목록 상태 필터가 다르므로 라벨·건수·이동 대상을 분리해야 클릭 시 빈 화면이 안 뜬다.
  const needInfoCount = attention.filter((o) => o.companyFacingStatus === '확인 필요').length
  const problemCount = attention.filter((o) => o.companyFacingStatus === '배송 문제').length
  const urgentParts: string[] = []
  // 결제 필요가 맨 앞 — 결제 전엔 준비가 시작되지 않아 가장 급하다(BIZ-78 후속).
  if (unpaid > 0) urgentParts.push(`결제 필요 ${unpaid}건`)
  if (needInfoCount > 0) urgentParts.push(`확인 필요 ${needInfoCount}건`)
  if (problemCount > 0) urgentParts.push(`배송 문제 ${problemCount}건`)
  // 다가오는 배송·예약 리마인더(차분한 스트립). 임박한 순서로: 오늘 → 내일 → 예약 임박(모레~글피).
  const reminderParts: string[] = []
  if (today > 0) reminderParts.push(`오늘 도착 ${today}건`)
  if (tomorrow > 0) reminderParts.push(`내일 ${tomorrow}건`)
  if (soon > 0) reminderParts.push(`예약 임박 ${soon}건`)
  // 클릭 시 결과가 반드시 있는 필터로 보낸다(확인 필요 우선, 없으면 배송 문제).
  const alertStatus = needInfoCount > 0 ? '확인 필요' : problemCount > 0 ? '배송 문제' : undefined

  // 캘린더: 희망일(desiredDate)로 날짜별 주문을 모은다.
  const byDate = new Map<string, OrderListItem[]>()
  for (const o of calData.data?.items ?? []) {
    if (!o.desiredDate) continue
    const key = o.desiredDate.slice(0, 10)
    const arr = byDate.get(key)
    if (arr) arr.push(o)
    else byDate.set(key, [o])
  }
  const todayStr = localDateStr(new Date())
  const selectedOrders = selectedDay ? byDate.get(selectedDay) ?? [] : []

  const shiftMonth = (delta: number) => {
    setSelectedDay(null)
    setCalMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1))
  }

  return (
    <div className={s.page}>
      {/* 1. 주문하기 (히어로) — 딥 티일 나이트 카드 + 국화 라인아트(카드 페이지와 같은 비주얼 계열).
          라이트/다크 무관하게 스스로 어두운 카드로 완결된 디자인이라 색을 직접 쓴다. */}
      <section>
        <div className={s.hero}>
          <svg className={s.heroArt} viewBox="0 0 200 200" aria-hidden="true">
            <g fill="none" stroke="currentColor" strokeWidth="1.1">
              {Array.from({ length: 16 }, (_, i) => (
                <ellipse key={i} cx="100" cy="58" rx="9" ry="42" transform={`rotate(${i * 22.5} 100 100)`} />
              ))}
              <circle cx="100" cy="100" r="13" />
            </g>
          </svg>
          <p className={s.heroKicker}>FORBIZ</p>
          <h1 className={s.heroTitle}>
            마음을 전하는
            <br />
            가장 빠른 방법
          </h1>
          <p className={s.heroSub}>바쁜 순간에도, 마음은 제때 전하도록</p>
          <button type="button" className={s.hgo} onClick={() => goOrder()}>
            새 주문 시작
            <span className={s.hgoArrow} aria-hidden="true">
              →
            </span>
          </button>
          {/* 작성 중 임시저장 — '이어서 작성'도 결국 주문 행위라 히어로 안 보조 액션으로.
              별도 카드로 두면 히어로·배너·알림과 주목 경쟁만 한다. */}
          {data?.draft ? (
            <button type="button" className={s.heroDraft} onClick={() => navigate(`/orders/${data.draft!.id}/edit`)}>
              <span className={s.heroDraftIcon} aria-hidden="true">{IC.edit}</span>
              <span className={s.heroDraftLabel}>
                {['작성 중인 주문', occasionLabel[data.draft.occasionType], data.draft.title]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              <span className={s.heroDraftGo}>이어서 작성 →</span>
            </button>
          ) : null}
        </div>
      </section>

      {/* 긴급 알림 (확인 필요·문제) — 가장 급한 소식이라 배너보다 위. */}
      {urgentParts.length > 0 ? (
        <button
          type="button"
          className={s.alert}
          onClick={() =>
            navigate('/orders', alertStatus ? { state: { companyStatus: alertStatus } } : undefined)
          }
        >
          <span className={s.alertIcon} aria-hidden="true">{IC.warn}</span>
          <span>{urgentParts.join(' · ')}</span>
          <span className={s.alertLink}>확인하기 →</span>
        </button>
      ) : null}

      {/* 다가오는 배송·예약 리마인더 — 긴급(앰버)과 달리 차분한 톤. 막지 않는 스트립.
          예약이 다가오면(모레·글피) 여기서 미리 알린다. 클릭 시 주문 목록으로. */}
      {reminderParts.length > 0 ? (
        <button type="button" className={s.softAlert} onClick={() => navigate('/orders')}>
          <span className={s.alertIcon} aria-hidden="true">{IC.calendar}</span>
          <span>{reminderParts.join(' · ')}</span>
          <span className={s.alertLink}>보기 →</span>
        </button>
      ) : null}

      {/* 사업자 검증·후불 안내(BIZ-85 → BIZ-149) — 기업만. 후불(ACTIVE)이 아니면 후불 상태·검증 상태별로 문구가 바뀐다. */}
      {data?.subjectType === 'COMPANY' && data.postpaidStatus != null && data.postpaidStatus !== 'ACTIVE' ? (
        <button type="button" className={s.softAlert} onClick={() => navigate('/settings/company')}>
          {data.postpaidStatus === 'SUSPENDED' ? (
            <>
              <span className={s.alertIcon} aria-hidden="true">{IC.warn}</span>
              <span>미납 청구가 있어 후불이 잠시 멈췄어요 — 지금은 선불로 주문하고, 완납하면 다시 열려요.</span>
              <span className={s.alertLink}>확인 →</span>
            </>
          ) : data.postpaidStatus === 'REQUESTED' ? (
            <>
              <span className={s.alertIcon} aria-hidden="true">{IC.card}</span>
              <span>후불(월 청구) 신청을 검토하고 있어요 — 그동안은 선불로 주문해요.</span>
              <span className={s.alertLink}>자세히 →</span>
            </>
          ) : data.postpaidStatus === 'REJECTED' ? (
            <>
              <span className={s.alertIcon} aria-hidden="true">{IC.card}</span>
              <span>후불 신청이 반려됐어요 — 정보를 보완해 다시 신청할 수 있어요.</span>
              <span className={s.alertLink}>다시 신청 →</span>
            </>
          ) : data.bizVerifyStatus === 'FAILED' ? (
            <>
              <span className={s.alertIcon} aria-hidden="true">{IC.warn}</span>
              <span>사업자 정보를 확인하지 못했어요 — 휴·폐업이거나 정보가 달라요. 고객센터로 문의해 주세요.</span>
              <span className={s.alertLink}>문의 →</span>
            </>
          ) : data.bizVerifyStatus === 'VERIFIED' ? (
            <>
              <span className={s.alertIcon} aria-hidden="true">{IC.check}</span>
              <span>사업자 확인 완료 — 지금은 선불이에요. 법인 사업자는 내 회사에서 월 청구(후불)를 신청할 수 있어요.</span>
              <span className={s.alertLink}>후불 신청 →</span>
            </>
          ) : (
            <>
              <span className={s.alertIcon} aria-hidden="true">{IC.card}</span>
              <span>지금은 선불 결제로 이용해요 — 법인 사업자는 월 청구(후불)를 신청할 수 있어요.</span>
              <span className={s.alertLink}>자세히 →</span>
            </>
          )}
        </button>
      ) : null}

      {/* 알림 수신번호 미설정 — 이대로면 주문 알림톡이 못 나간다. 설정 권한이 있는 관리자에게만. */}
      {data && !data.notifyPhoneSet && user?.role === 'COMPANY_ADMIN' ? (
        <button type="button" className={s.softAlert} onClick={() => navigate('/settings/company')}>
          <span className={s.alertIcon} aria-hidden="true">{IC.bell}</span>
          <span>주문 알림을 받을 연락처가 없어요 — 알림 수신번호를 설정해 주세요.</span>
          <span className={s.alertLink}>설정 →</span>
        </button>
      ) : null}

      {/* 배너 (BIZ-111) — 운영자 관리, 활성·기간 내 배너만. 자동 슬라이드+수동 스와이프.
          추천(회의 S5)은 따로 떠 있던 카드를 캐러셀의 마지막 슬라이드로 합쳤다(코드 고정, 회사 사용자만). */}
      {/* 배너 목록이 온 뒤에 그린다 — 추천 슬라이드만 먼저 그려 두면 이미지 배너가 앞에 끼어들 때
          스냅이 추천 슬라이드를 붙잡아 첫 화면이 추천부터 보인다. */}
      {!bannersAsync.loading && (bannerItems.length > 0 || isCompany) ? (
        <BannerCarousel
          items={bannerItems}
          onOpen={openBanner}
          extraSlides={
            isCompany
              ? [
                  {
                    key: 'referral',
                    label: '지인에게 추천하고 포인트 받기',
                    onClick: () => navigate('/referral'),
                    content: <ReferralSlide />,
                  },
                ]
              : []
          }
        />
      ) : null}

      {/* 3. 최근 주문 + 캘린더 (토글) */}
      <section>
        <div className={s.sech}>
          <div className={s.sechTitle}>
            <h2>최근 주문</h2>
          </div>
          <div className={s.toggle} role="tablist" aria-label="최근 주문 보기 방식">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'list'}
              className={view === 'list' ? s.on : undefined}
              onClick={() => setView('list')}
            >
              리스트
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'calendar'}
              className={view === 'calendar' ? s.on : undefined}
              onClick={() => setView('calendar')}
            >
              캘린더
            </button>
          </div>
        </div>

        {view === 'list' ? (
          loading ? (
            <p className={s.muted}>불러오는 중…</p>
          ) : recent.length === 0 ? (
            <p className={s.muted}>아직 주문이 없습니다.</p>
          ) : (
            <div>
              <div className={s.olist}>
                {recent.slice(0, 4).map((o) => (
                  <OrderCard key={o.id} o={o} onOpen={() => navigate(`/orders/${o.id}`)} />
                ))}
              </div>
              <button type="button" className={s.more} style={{ marginTop: 'var(--space-md)' }} onClick={() => navigate('/orders')}>
                주문 내역 보기 →
              </button>
            </div>
          )
        ) : (
          <div className={s.cal}>
            <div className={s.calhd}>
              {/* 화살표는 텍스트 글리프(‹›) 대신 SVG — 폰트 따라 어긋나 보이는 문제 방지. */}
              <button type="button" className={s.navbtn} aria-label="이전 달" onClick={() => shiftMonth(-1)}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M14.5 6 8.5 12l6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <span className={s.calMonth}>
                {calMonth.getFullYear()}년 {calMonth.getMonth() + 1}월
              </span>
              <button type="button" className={s.navbtn} aria-label="다음 달" onClick={() => shiftMonth(1)}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="m9.5 6 6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
            <div className={s.calgrid}>
              {DOW.map((w, i) => (
                <span key={w} className={`${s.dow} ${i === 0 ? s.dowSun : i === 6 ? s.dowSat : ''}`}>
                  {w}
                </span>
              ))}
              {cells.map((d) => {
                const key = localDateStr(d)
                const inMonth = d.getMonth() === calMonth.getMonth()
                const dayOrders = byDate.get(key) ?? []
                const has = dayOrders.length > 0
                const holidayName = holidayMap[key]
                // 주말·공휴일 색은 이번 달 날짜에만(인접월은 dim 회색 유지). 일요일·공휴일=빨강, 토요일=파랑.
                const cls = [
                  s.day,
                  inMonth ? '' : s.dim,
                  inMonth && (d.getDay() === 0 || holidayName) ? s.sun : '',
                  inMonth && d.getDay() === 6 && !holidayName ? s.sat : '',
                  key === todayStr ? s.today : '',
                  key === selectedDay ? s.sel : '',
                  has ? s.clickable : '',
                ]
                  .filter(Boolean)
                  .join(' ')
                return (
                  <button
                    key={key}
                    type="button"
                    className={cls}
                    disabled={!has}
                    title={holidayName}
                    onClick={() => setSelectedDay(key)}
                  >
                    <span className={s.dnum}>{d.getDate()}</span>
                    {has ? (
                      <span
                        className={`${s.dlabel} ${key <= todayStr ? s.dlabelPast : s.dlabelFuture}`}
                      >
                        {dayOrders[0]!.title || '주문'}
                      </span>
                    ) : null}
                    {dayOrders.length > 1 ? <span className={s.dmore}>+{dayOrders.length - 1}</span> : null}
                  </button>
                )
              })}
            </div>
            <div className={s.calleg}>
              <span>
                <i className={s.dotPast} />
                지난 주문
              </span>
              <span>
                <i className={s.dotFuture} />
                예약(배송 희망일)
              </span>
            </div>
            {selectedDay ? (
              <div className={s.dayOrders}>
                <div className={s.dayOrdersHd}>
                  {fmtDate(selectedDay)} · {selectedOrders.length}건
                </div>
                {selectedOrders.length === 0 ? (
                  <p className={s.muted}>이 날 주문이 없어요.</p>
                ) : (
                  selectedOrders.map((o) => (
                    <OrderCard key={o.id} o={o} onOpen={() => navigate(`/orders/${o.id}`)} />
                  ))
                )}
              </div>
            ) : null}
          </div>
        )}
      </section>

      {/* 3.5 상품 둘러보기 */}
      {products.length > 0 ? (
        <section>
          <div className={s.sech}>
            <h2>상품 둘러보기</h2>
            <button type="button" className={s.more} onClick={() => navigate('/products')}>
              전체 보기 →
            </button>
          </div>
          {categories.length > 0 ? (
            <div className={s.catrow}>
              {categories.map((c, i) => (
                <button
                  key={c.occ}
                  type="button"
                  className={s.catcard}
                  style={{ '--i': i } as CSSProperties}
                  onClick={() => navigate(`/products?occasion=${c.occ}`)}
                >
                  {/* base64 data URI라 url()을 큰따옴표로 감싼다(안 감싸면 CSS 무효화). */}
                  <span
                    className={`${s.catim} ${s[`tile_${c.occ}`]}`}
                    style={{ backgroundImage: `url("${CATEGORY_ART[c.occ]}")` }}
                    aria-hidden="true"
                  />
                  <span className={s.catt}>{c.title}</span>
                </button>
              ))}
            </div>
          ) : null}
          <div className={s.prow}>
            {products.slice(0, 8).map((p: CatalogItem) => (
              <button
                key={p.productId}
                type="button"
                className={s.pcard}
                onClick={() => navigate(`/products/${p.productId}`)}
              >
                <span
                  className={s.pim}
                  style={{ backgroundImage: `url(${p.imageUrl ?? defaultProductImage})` }}
                  aria-hidden="true"
                />
                <span className={s.pn}>{p.name}</span>
                <span className={s.pp}>{p.price.toLocaleString('ko-KR')}원</span>
              </button>
            ))}
          </div>
          <p className={s.note}>전체 보기에서 골라 담고, 마음에 드는 화환끼리 비교해볼 수 있어요.</p>
        </section>
      ) : null}

      {/* 4. 내 디지털 상품 — 디지털 화환 진입점(BIZ-125. 옛 이름 '디지털 근조기'). */}
      <section>
        <div className={`${s.sech} ${s.digitalHead}`}>
          <h2>내 디지털 상품</h2>
        </div>
        <button type="button" className={s.digitalEntry} onClick={() => navigate('/digital-wreath')}>
          <span className={s.digitalEntryArt} aria-hidden="true">
            <svg viewBox="0 0 100 100" aria-hidden="true">
              <g fill="currentColor">
                {Array.from({ length: 12 }, (_, i) => (
                  <ellipse key={`o${i}`} cx="50" cy="30" rx="5" ry="14" transform={`rotate(${i * 30} 50 50)`} />
                ))}
              </g>
              <circle cx="50" cy="50" r="6.5" fill="currentColor" opacity="0.6" />
            </svg>
          </span>
          <span className={s.digitalEntryBody}>
            <span className={s.digitalEntryTitle}>디지털 화환</span>
            <span className={s.digitalEntrySub}>내 디지털 화환 디자인 설정 · 제휴 장례식장에 함께 올라가요</span>
          </span>
          <span className={s.digitalEntryGo} aria-hidden="true">→</span>
        </button>
      </section>

      <Footer />
    </div>
  )
}
